
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { fetchSwingTendencies } from '@/utils/puttingResults/fetchUtils';
import CombinedIronChart from './swing/CombinedIronChart';
import CombinedFairwayWoodChart from './swing/CombinedFairwayWoodChart';
import { SwingTendency } from './swing/types';
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface SwingDistanceChartsProps {
  selectedLevel: string | null;
}

const SwingDistanceCharts: React.FC<SwingDistanceChartsProps> = ({
  selectedLevel
}) => {
  const [tendencies, setTendencies] = useState<SwingTendency[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch tendencies directly from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Get the current user
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          console.error("No authenticated user found");
          setLoading(false);
          return;
        }
        
        // Fetch swing tendencies
        const tendenciesData = await fetchSwingTendencies(session.user.id);
        console.log("Fetched swing tendencies:", tendenciesData.length);
        
        setTendencies(tendenciesData);
      } catch (error) {
        console.error("Error fetching swing tendencies:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter tendencies by type
  const ironTendencies = tendencies.filter(t => t.club_type === 'iron');
  const fairwayWoodTendencies = tendencies.filter(t => t.club_type === 'fairwayWood');
  
  // If loading, show a loading indicator
  if (loading) {
    return (
      <Card className="shadow-md bg-white">
        <CardContent className="p-6 text-center">
          <div className="flex justify-center my-4">
            <Loader2 className="h-6 w-6 text-green-600 animate-spin" />
          </div>
          <p className="text-gray-500">Chargement des tendances de swing...</p>
        </CardContent>
      </Card>
    );
  }
  
  // If no data, show a message
  if (tendencies.length === 0) {
    return (
      <Card className="shadow-md bg-white">
        <CardContent className="p-6 text-center">
          <h3 className="text-lg font-semibold mb-2">Tendances de swing</h3>
          <p className="text-gray-500">Aucune donnée d'entraînement disponible.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div>
      <h3 className="text-lg font-bold mb-6 text-gray-800 px-1 border-l-4 border-green-500 pl-3">
        Tendances de swing
        {selectedLevel && <span className="ml-2 text-sm font-normal text-gray-600">(Niveau {selectedLevel})</span>}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Iron Chart */}
        <CombinedIronChart 
          selectedLevel={selectedLevel}
          tendencies={ironTendencies}
        />
        
        {/* Fairway Wood Chart */}
        <CombinedFairwayWoodChart 
          selectedLevel={selectedLevel}
          tendencies={fairwayWoodTendencies}
        />
      </div>
    </div>
  );
};

export default SwingDistanceCharts;
